// src/components/onboarding/OnboardingGuide.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Rocket,
  Check,
  LayoutDashboard,
  LifeBuoy,
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { getAccessibleModules } from '../../config/modules';
import { SETTINGS_TABS } from '../../config/settingsTabs';
import styles from './OnboardingGuide.module.css';

// ─────────────────────────────────────────────────────────────
// PERSISTANCE (localStorage, par utilisateur)
// ─────────────────────────────────────────────────────────────
const STORAGE_PREFIX = 'cvtoskill_onboarding_seen_';

const hasSeenGuide = (userId) => {
  if (!userId) return true; // par sécurité, on n'affiche rien tant qu'on ne connaît pas l'utilisateur
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + userId) === '1';
  } catch {
    return true;
  }
};

const markGuideSeen = (userId) => {
  if (!userId) return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + userId, '1');
  } catch {
    /* localStorage indisponible (navigation privée, quota…) : on ignore silencieusement */
  }
};

// ─────────────────────────────────────────────────────────────
// ÉVÉNEMENT GLOBAL — permet de relancer la visite depuis n'importe où
// (bouton "Revoir la visite guidée", lien d'aide dans la Navbar, etc.)
// ─────────────────────────────────────────────────────────────
const OPEN_EVENT = 'cvtoskill:open-onboarding-guide';

export const openOnboardingGuide = () => {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
};

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const OnboardingGuide = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);

  const accessibleModules = useMemo(() => getAccessibleModules(user), [user]);

  // Diapositives : Bienvenue → un écran par module accessible
  // → un écran par onglet de configuration (toujours visibles, non liés aux permissions) → Conclusion
  const slides = useMemo(() => {
    const moduleSlides   = accessibleModules.map((m) => ({ type: 'module',   item: m }));
    const settingsSlides = SETTINGS_TABS.map((s)     => ({ type: 'settings', item: s }));
    return [{ type: 'welcome' }, ...moduleSlides, ...settingsSlides, { type: 'finish' }];
  }, [accessibleModules]);

  // Ouverture automatique lors de la toute première connexion de cet utilisateur
  useEffect(() => {
    if (!user) return;
    if (!hasSeenGuide(user.id)) {
      setStep(0);
      setIsOpen(true);
    }
  }, [user]);

  // Ouverture manuelle (bouton "Revoir la visite guidée")
  useEffect(() => {
    const handleOpen = () => {
      setStep(0);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_EVENT, handleOpen);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    markGuideSeen(user?.id);
  }, [user]);

  const goNext = useCallback(
    () => setStep((s) => Math.min(s + 1, slides.length - 1)),
    [slides.length]
  );
  const goPrev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  // Focus + navigation clavier : Échap ferme, ← / → change de diapositive,
  // Tab reste piégé à l'intérieur de la modale (accessibilité)
  const dialogRef = useRef(null);
  const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    if (!isOpen) return undefined;

    // Place le focus initial dans la modale à l'ouverture
    const dialogEl = dialogRef.current;
    const firstFocusable = dialogEl?.querySelector(FOCUSABLE);
    (firstFocusable || dialogEl)?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key === 'ArrowRight') {
        goNext();
        return;
      }
      if (e.key === 'ArrowLeft') {
        goPrev();
        return;
      }
      if (e.key === 'Tab' && dialogEl) {
        const focusables = Array.from(dialogEl.querySelectorAll(FOCUSABLE));
        if (!focusables.length) return;
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close, goNext, goPrev]);

  // Bloque le scroll de la page pendant que la visite est ouverte
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !user) return null;

  const firstName = user?.prenom || user?.email?.split('@')[0] || '';
  const current   = slides[step];
  const isFirst   = step === 0;
  const isLast    = step === slides.length - 1;

  return createPortal(
    <div className={styles.backdrop} onMouseDown={close}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Visite guidée de CVtoSkill"
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={close}
          aria-label="Fermer la visite guidée"
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div className={styles.content}>
          {current.type === 'welcome' && (
            <WelcomeSlide firstName={firstName} moduleCount={accessibleModules.length} />
          )}

          {current.type === 'module' && (
            <FeatureSlide
              badgeLabel="Module"
              ctaLabel="Découvrir ce module"
              item={current.item}
              index={accessibleModules.findIndex((m) => m.key === current.item.key) + 1}
              total={accessibleModules.length}
              onClose={close}
            />
          )}

          {current.type === 'settings' && (
            <FeatureSlide
              badgeLabel="Configuration"
              ctaLabel="Configurer maintenant"
              item={current.item}
              index={SETTINGS_TABS.findIndex((s) => s.key === current.item.key) + 1}
              total={SETTINGS_TABS.length}
              onClose={close}
            />
          )}

          {current.type === 'finish' && (
            <FinishSlide hasModules={accessibleModules.length > 0} onClose={close} />
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.dots} aria-hidden="true">
            {slides.map((s, i) => (
              <span
                key={`${s.type}-${i}`}
                className={`${styles.dot} ${i === step ? styles.dotActive : ''}`}
              />
            ))}
          </div>

          <div className={styles.actions}>
            {!isFirst && (
              <button type="button" className={styles.btnGhost} onClick={goPrev}>
                <ArrowLeft size={15} strokeWidth={2} />
                Précédent
              </button>
            )}

            {isFirst && (
              <button type="button" className={styles.btnGhost} onClick={close}>
                Passer
              </button>
            )}

            {!isLast ? (
              <button type="button" className={styles.btnPrimary} onClick={goNext}>
                Suivant
                <ArrowRight size={15} strokeWidth={2} />
              </button>
            ) : (
              <button type="button" className={styles.btnPrimary} onClick={close}>
                <Check size={15} strokeWidth={2.5} />
                Terminer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────
// DIAPOSITIVE : BIENVENUE
// ─────────────────────────────────────────────────────────────
const WelcomeSlide = ({ firstName, moduleCount }) => (
  <div className={styles.slide}>
    <div className={`${styles.iconWrap} ${styles.iconWrapNeutral}`}>
      <Sparkles size={26} strokeWidth={1.75} />
    </div>
    <h2 className={styles.title}>
      Bienvenue{firstName ? `, ${firstName}` : ''} 👋
    </h2>
    {moduleCount > 0 ? (
      <p className={styles.desc}>
        Votre compte donne accès à <strong>{moduleCount} module{moduleCount > 1 ? 's' : ''}</strong>.
        Faisons un tour rapide pour vous montrer ce que vous pouvez en faire, ainsi que les
        options de configuration disponibles.
      </p>
    ) : (
      <p className={styles.desc}>
        Votre compte est actif, mais aucun module n'est encore activé.
        Votre administrateur peut vous donner accès aux fonctionnalités depuis l'espace Pilotage.
        En attendant, faisons un tour des options de configuration déjà disponibles.
      </p>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// DIAPOSITIVE : UN MODULE OU UN ONGLET DE CONFIGURATION
// ─────────────────────────────────────────────────────────────
const FeatureSlide = ({ item, badgeLabel, ctaLabel, index, total, onClose }) => {
  const { title, desc, path, Icon, color } = item;
  return (
    <div className={styles.slide} data-color={color}>
      <span className={styles.stepBadge}>{badgeLabel} {index} / {total}</span>
      <div className={styles.iconWrap}>
        <Icon size={26} strokeWidth={1.75} />
      </div>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.desc}>{desc}</p>
      <Link to={path} className={styles.tryLink} onClick={onClose}>
        {ctaLabel}
        <ArrowRight size={14} strokeWidth={2} />
      </Link>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DIAPOSITIVE : CONCLUSION
// ─────────────────────────────────────────────────────────────
const FinishSlide = ({ hasModules, onClose }) => (
  <div className={styles.slide}>
    <div className={`${styles.iconWrap} ${styles.iconWrapNeutral}`}>
      <Rocket size={26} strokeWidth={1.75} />
    </div>
    <h2 className={styles.title}>
      {hasModules ? 'Vous êtes prêt·e !' : 'Une question, un besoin ?'}
    </h2>
    {hasModules ? (
      <p className={styles.desc}>
        Retrouvez vos modules à tout moment depuis la page d'accueil ou le menu
        <strong> « Nos Modules »</strong>, et vos réglages depuis le menu
        <strong> « Configuration »</strong> en haut de l'écran. L'espace
        <strong> Pilotage</strong> vous permet de suivre votre activité.
      </p>
    ) : (
      <p className={styles.desc}>
        Si vous pensez qu'un module devrait être actif sur votre compte, contactez
        votre administrateur ou notre équipe support.
      </p>
    )}
    <div className={styles.finishLinks}>
      <Link to="/management" className={styles.finishLink} onClick={onClose}>
        <LayoutDashboard size={14} strokeWidth={2} />
        Pilotage
      </Link>
      <Link to="/support" className={styles.finishLink} onClick={onClose}>
        <LifeBuoy size={14} strokeWidth={2} />
        Aide &amp; support
      </Link>
    </div>
  </div>
);

export default OnboardingGuide;